import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ShieldCheck, Mail, Phone, User, BadgeCheck, CalendarClock, Activity, Pencil, LockKeyhole, Laptop, Smartphone, UploadCloud, X, KeyRound, Megaphone } from 'lucide-react'
import { Modal } from '../../common/Modal/Modal'
import { fetchProfile, updateProfile, changePassword } from '../AdminProfile/services/profileSlice'

const sessions = [
  { device: 'MacBook Pro · Chrome', location: 'Mumbai, IN', time: 'Active now', icon: <Laptop size={14} /> },
  { device: 'iPhone 15 · Safari',   location: 'Mumbai, IN', time: '2 hours ago', icon: <Smartphone size={14} /> },
  { device: 'Windows PC · Edge',    location: 'Pune, IN',   time: 'Yesterday',   icon: <Laptop size={14} /> },
]

const formatJoined = (date) => {
  if (!date) return '—'
  return new Date(date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

export default function AdminProfile() {
  const dispatch = useDispatch()
  const fileInputRef = useRef(null)

  const { profile, isLoading, isSaving } = useSelector((state) => state.profile)
  const saving = isSaving

  const [editOpen, setEditOpen] = useState(false)
  const [passwordOpen, setPasswordOpen] = useState(false)
  const [toast, setToast] = useState({ type: '', text: '' })
  const [formError, setFormError] = useState('')
  const [pwdError, setPwdError] = useState('')

  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    avatarPreview: '', avatarName: '', avatarFile: null, removeImage: false,
    topbar_enabled: false, topbar_text: '', topbar_code: '',
  })

  const [pwd, setPwd] = useState({ current_password: '', new_password: '', new_password_confirmation: '' })

  useEffect(() => {
    dispatch(fetchProfile())
  }, [dispatch])

  const showToast = (type, text) => {
    setToast({ type, text })
    setTimeout(() => setToast({ type: '', text: '' }), 3000)
  }

  const initials = (profile?.name || 'AD')
    .split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()

  /* ---------- edit modal ---------- */
  const openEdit = () => {
    setForm({
      name: profile.name || '',
      email: profile.email || '',
      phone: profile.phone || '',
      avatarPreview: profile.profile_image || '',
      avatarName: profile.profile_image ? 'Current photo' : '',
      avatarFile: null,
      removeImage: false,
      // ✅ DB ni real value (jya sudhi tame OFF na karo tya sudhi ON j rahese)
      topbar_enabled: profile.topbar_enabled === true || profile.topbar_enabled === 1 || profile.topbar_enabled === '1',
      topbar_text: profile.topbar_text || '',
      topbar_code: profile.topbar_code || '',
    })
    setFormError('')
    setEditOpen(true)
  }

  const handleAvatarUpload = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      setFormError('Image must be less than 5 MB')
      return
    }
    setFormError('')
    setForm((prev) => ({
      ...prev,
      avatarPreview: URL.createObjectURL(file),
      avatarName: file.name,
      avatarFile: file,
      removeImage: false,
    }))
  }

  const clearAvatar = (e) => {
    e.stopPropagation()
    setForm((prev) => ({
      ...prev, avatarPreview: '', avatarName: '', avatarFile: null,
      removeImage: !!profile.profile_image,
    }))
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const saveProfile = async () => {
    setFormError('')

    // ON hoy to text ke code ma thi ek to joiye j
    if (form.topbar_enabled && !form.topbar_text.trim() && !form.topbar_code.trim()) {
      setFormError('Announcement ON chhe, to text ke promo code ma thi ek lakhvu pade')
      return
    }

    const fd = new FormData()
    fd.append('name', form.name)
    fd.append('email', form.email)
    fd.append('phone', form.phone || '')

    // ✅ Hamesha current state save thase (toggle ma je dekhay te j DB ma jase)
    fd.append('topbar_enabled', form.topbar_enabled ? '1' : '0')
    fd.append('topbar_text', form.topbar_text || '')
    fd.append('topbar_code', form.topbar_code || '')

    if (form.avatarFile) fd.append('profile_image', form.avatarFile)
    if (form.removeImage) fd.append('remove_image', '1')

    try {
      const result = await dispatch(updateProfile(fd)).unwrap()
      setEditOpen(false)
      showToast('success', result.message)
    } catch (errMsg) {
      setFormError(errMsg)
    }
  }

  /* ---------- change password ---------- */
  const openPassword = () => {
    setPwd({ current_password: '', new_password: '', new_password_confirmation: '' })
    setPwdError('')
    setPasswordOpen(true)
  }

  const updatePassword = async () => {
    setPwdError('')
    if (pwd.new_password !== pwd.new_password_confirmation) {
      setPwdError('New password and confirm password do not match')
      return
    }
    try {
      const message = await dispatch(changePassword(pwd)).unwrap()
      setPasswordOpen(false)
      showToast('success', message)
    } catch (errMsg) {
      setPwdError(errMsg)
    }
  }

  if (isLoading && !profile?.username) {
    return (
      <div className="dash-page page-in">
        <h1 className="page-title">Admin Profile</h1>
        <p style={{ color: 'var(--text2)' }}>Loading profile...</p>
      </div>
    )
  }

  return (
    <div className="dash-page page-in">
      <h1 className="page-title">Admin Profile</h1>

      {toast.text && <div className={`profile-toast ${toast.type}`}>{toast.text}</div>}

      <section className="card profile-hero">
        <div className="profile-hero-left">
          <div className="profile-avatar">
            {profile.profile_image ? <img src={profile.profile_image} alt="Admin avatar" /> : initials}
          </div>
          <div>
            <h2>{profile.name}</h2>
            <p>{profile.login_type} · {profile.username}</p>
            <div className="profile-tags">
              <span className="badge badge-active"><BadgeCheck size={12} /> Verified</span>
              <span className="badge badge-gold"><CalendarClock size={12} /> Joined {formatJoined(profile.created_at)}</span>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
          <button className="btn btn-outline" onClick={openPassword}>
            <LockKeyhole size={14} /> Change Password
          </button>
          <button className="btn btn-primary" onClick={openEdit}>
            <Pencil size={14} /> Edit Profile
          </button>
        </div>
      </section>

      <div className="profile-content">
        <div className="profile-main">
          <section className="card profile-card">
            <div className="card-header">
              <div className="card-title">Personal Information</div>
              <div className="card-subtitle">Contact details and account info</div>
            </div>
            <div className="profile-details-grid">
              <div className="detail-item">
                <Mail size={16} />
                <div>
                  <span className="detail-label">Email</span>
                  <span className="detail-value">{profile.email || '—'}</span>
                </div>
              </div>
              <div className="detail-item">
                <Phone size={16} />
                <div>
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">{profile.phone || '—'}</span>
                </div>
              </div>
              <div className="detail-item">
                <User size={16} />
                <div>
                  <span className="detail-label">Username</span>
                  <span className="detail-value">{profile.username}</span>
                </div>
              </div>
              <div className="detail-item">
                <KeyRound size={16} />
                <div>
                  <span className="detail-label">Login Type</span>
                  <span className="detail-value">{profile.login_type || '—'}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <div className="card-title">Account Security</div>
              <ShieldCheck size={18} style={{ color: 'var(--color-green)' }} />
            </div>
            <div className="security-grid">
              <div className="security-item">
                <div className="security-icon"><ShieldCheck size={20} /></div>
                <div>
                  <span className="security-label">Account Status</span>
                  <span className="security-status ok">{profile.status == 1 ? 'Active' : 'Disabled'}</span>
                </div>
              </div>
              <div className="security-item">
                <div className="security-icon"><LockKeyhole size={20} /></div>
                <div>
                  <span className="security-label">Password</span>
                  <span className="security-status ok">••••••••</span>
                </div>
              </div>
              <div className="security-item">
                <div className="security-icon"><Mail size={20} /></div>
                <div>
                  <span className="security-label">Recovery Email</span>
                  <span className="security-status ok">{profile.email}</span>
                </div>
              </div>
              <div className="security-item">
                <div className="security-icon"><Megaphone size={20} /></div>
                <div>
                  <span className="security-label">Announcement Bar</span>
                  <span className="security-status ok">{profile.topbar_enabled ? 'ON' : 'OFF'}</span>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="profile-sidebar">
          <section className="card">
            <div className="card-header">
              <div className="card-title">Recent Activity</div>
            </div>
            <div className="activity-list">
              <div className="activity-item">
                <div className="activity-icon"><Activity size={16} /></div>
                <div>
                  <p className="activity-text">Logged in to dashboard</p>
                  <span className="activity-time">Just now</span>
                </div>
              </div>
            </div>
          </section>

          <section className="card">
            <div className="card-header">
              <div className="card-title">Active Sessions</div>
              <button className="btn btn-outline btn-sm">Sign out all</button>
            </div>
            <div className="sessions-list">
              {sessions.map((item) => (
                <div key={item.device} className="session-item">
                  <div className="session-icon">{item.icon}</div>
                  <div className="session-info">
                    <span className="session-device">{item.device}</span>
                    <span className="session-location">{item.location}</span>
                    <span className="session-time">{item.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      {/* ── EDIT PROFILE MODAL ── */}
      <Modal
        open={editOpen}
        title="Edit Profile"
        subtitle="Update your profile details"
        onClose={() => setEditOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setEditOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={saveProfile} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        )}
      >
        <div className="form-grid cols-2">
          {formError && <div className="form-field full form-error">{formError}</div>}

          <div className="form-field full">
            <label>Profile Photo</label>
            <div
              className={`avatar-upload-zone ${form.avatarPreview ? 'has-preview' : ''}`}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleAvatarUpload}
              />

              {form.avatarPreview ? (
                <>
                  <div className="auz-preview-img">
                    <img src={form.avatarPreview} alt="Preview" />
                  </div>
                  <div className="auz-preview-info">
                    <span className="auz-fname">{form.avatarName}</span>
                    <span className="auz-change">Click to change photo</span>
                  </div>
                  <button className="auz-clear" onClick={clearAvatar} title="Remove photo">
                    <X size={12} />
                  </button>
                </>
              ) : (
                <>
                  <div className="auz-icon"><UploadCloud size={22} /></div>
                  <div className="auz-text">
                    <span className="auz-primary">Click to upload photo</span>
                    <span className="auz-hint">PNG, JPG or GIF — max 5 MB</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-field">
            <label>Full Name</label>
            <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter full name" />
          </div>
          <div className="form-field">
            <label>Email Address</label>
            <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Enter email" />
          </div>
          <div className="form-field full">
            <label>Phone Number</label>
            <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Enter phone number" />
          </div>

          {/* ── Announcement Bar (website topbar) ── */}
          <div className="form-field full">
            <div className={`topbar-card ${form.topbar_enabled ? 'is-on' : ''}`}>
              <div className="topbar-card-head">
                <div className="topbar-card-icon"><Megaphone size={16} /></div>
                <div className="topbar-card-info">
                  <span className="topbar-card-title">Announcement Bar</span>
                  <span className="topbar-card-sub">
                    {form.topbar_enabled ? 'ON – It is displayed on the website' : 'OFF – It is not displayed on the website'}
                  </span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.topbar_enabled}
                  aria-label="Show announcement bar"
                  className={`switch ${form.topbar_enabled ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, topbar_enabled: !form.topbar_enabled })}
                >
                  <span className="switch-knob" />
                </button>
              </div>

              {form.topbar_enabled && (
                <div className="topbar-card-body">
                  <div className="form-field">
                    <label>Announcement Text</label>
                    <input
                      className="input"
                      maxLength={150}
                      value={form.topbar_text}
                      onChange={(e) => setForm({ ...form, topbar_text: e.target.value })}
                      placeholder="FREE Shipping On Orders Above ₹999"
                    />
                  </div>
                  <div className="form-field">
                    <label>Promo Code <span className="topbar-optional">(optional)</span></label>
                    <input
                      className="input"
                      maxLength={40}
                      value={form.topbar_code}
                      onChange={(e) => setForm({ ...form, topbar_code: e.target.value })}
                      placeholder="SUPADAA10"
                    />
                  </div>

                  {(form.topbar_text || form.topbar_code) && (
                    <div className="topbar-preview">
                      <span className="topbar-preview-label">Preview</span>
                      <div className="topbar-preview-strip">
                        {form.topbar_text && <span>{form.topbar_text}</span>}
                        {form.topbar_text && form.topbar_code && <span className="tp-divider">|</span>}
                        {form.topbar_code && <span>Use Code: <b>{form.topbar_code}</b></span>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* ── CHANGE PASSWORD MODAL ── */}
      <Modal
        open={passwordOpen}
        title="Change Password"
        subtitle="Secure your account with a new password"
        onClose={() => setPasswordOpen(false)}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setPasswordOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={updatePassword} disabled={saving}>
              {saving ? 'Updating...' : 'Update Password'}
            </button>
          </>
        )}
      >
        <div className="form-grid">
          {pwdError && <div className="form-field form-error">{pwdError}</div>}
          <div className="form-field">
            <label>Current Password</label>
            <input className="input" type="password" value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              placeholder="Enter current password" />
          </div>
          <div className="form-field">
            <label>New Password</label>
            <input className="input" type="password" value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              placeholder="Enter new password (min 6 chars)" />
          </div>
          <div className="form-field">
            <label>Confirm New Password</label>
            <input className="input" type="password" value={pwd.new_password_confirmation}
              onChange={(e) => setPwd({ ...pwd, new_password_confirmation: e.target.value })}
              placeholder="Confirm new password" />
          </div>
        </div>
      </Modal>

     <style>{`
        .dash-page { display: flex; flex-direction: column; gap: var(--space-lg); width: 100%; }
        .profile-hero {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--space-md);
          padding: clamp(1rem, 2vw, 1.3rem);
          background: linear-gradient(135deg, rgba(79,70,229,.2), rgba(6,182,212,.12));
          border: 1px solid var(--border-active);
          border-radius: var(--radius-lg);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          overflow: hidden;
        }
        .profile-hero-left {
          display: flex;
          align-items: center;
          gap: var(--space-md);
          min-width: 0;
        }
        .profile-hero h2 { color: var(--text1); font-size: var(--fs-2xl); letter-spacing: -.02em; }
        .profile-hero p { color: var(--text2); font-size: var(--fs-sm); margin-top: 2px; }
        .profile-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
        .profile-card { display: flex; flex-direction: column; gap: var(--space-md); }
        .profile-avatar {
          width: 52px; height: 52px;
          border-radius: var(--radius-full);
          display: grid; place-items: center;
          font-size: var(--fs-md); font-weight: 700; color: #fff;
          background: linear-gradient(135deg, var(--accent), var(--accent-2));
          overflow: hidden;
          flex-shrink: 0;
        }
        .profile-avatar img { width: 100%; height: 100%; object-fit: cover; }
        .profile-content {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: var(--space-lg);
          align-items: start;
        }
        .profile-main { display: flex; flex-direction: column; gap: var(--space-lg); }
        .profile-sidebar { display: flex; flex-direction: column; gap: var(--space-lg); }
        .profile-details-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-lg);
        }
        .detail-item {
          display: flex; align-items: center; gap: var(--space-md);
          padding: var(--space-md);
          background: var(--bg-raised);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
        }
        .detail-item svg { color: var(--accent); flex-shrink: 0; }
        .detail-label {
          font-size: var(--fs-xs); color: var(--text3);
          text-transform: uppercase; letter-spacing: 0.5px;
          display: block; margin-bottom: 2px;
        }
        .detail-value { font-size: var(--fs-sm); color: var(--text1); font-weight: 500; }
        .security-grid { display: grid; gap: var(--space-md); }
        .security-item {
          display: flex; align-items: center; gap: var(--space-md);
          padding: var(--space-md);
          background: var(--bg-raised);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
        }
        .security-icon {
          width: 40px; height: 40px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: rgba(16,185,129,0.1);
          color: var(--color-green);
          border: 1px solid rgba(16,185,129,0.2);
          flex-shrink: 0;
        }
        .security-label { font-size: var(--fs-sm); color: var(--text2); display: block; margin-bottom: 2px; }
        .security-status.ok { color: var(--color-green); font-weight: 600; font-size: var(--fs-xs); }
        .activity-list { display: flex; flex-direction: column; gap: var(--space-md); }
        .activity-item { display: flex; align-items: flex-start; gap: var(--space-md); padding: var(--space-sm) 0; }
        .activity-icon {
          width: 32px; height: 32px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: var(--bg-raised); color: var(--accent);
          border: 1px solid var(--border); flex-shrink: 0;
        }
        .activity-text { font-size: var(--fs-sm); color: var(--text2); margin-bottom: 2px; }
        .activity-time { font-size: var(--fs-xs); color: var(--text4); }
        .sessions-list { display: flex; flex-direction: column; gap: var(--space-md); }
        .session-item {
          display: flex; align-items: center; gap: var(--space-md);
          padding: var(--space-md);
          background: var(--bg-raised);
          border-radius: var(--radius-md);
          border: 1px solid var(--border);
        }
        .session-icon {
          width: 36px; height: 36px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: rgba(79,70,229,0.1); color: var(--accent);
          border: 1px solid rgba(79,70,229,0.2);
          flex-shrink: 0;
        }
        .session-info { flex: 1; display: flex; flex-direction: column; gap: 2px; }
        .session-device { font-size: var(--fs-sm); color: var(--text1); font-weight: 500; display: block; }
        .session-location, .session-time { font-size: var(--fs-xs); color: var(--text3); display: block; }

        /* ── AVATAR UPLOAD ZONE ── */
        .avatar-upload-zone {
          position: relative;
          border: 2px dashed var(--border-active);
          border-radius: var(--radius-md);
          padding: 20px 16px;
          display: flex;
          align-items: center;
          gap: 14px;
          cursor: pointer;
          background: var(--bg-raised);
          transition: border-color 0.2s, background 0.2s;
          overflow: hidden;
        }
        .avatar-upload-zone:hover {
          border-color: var(--accent);
          background: var(--bg-active);
        }
        .avatar-upload-zone.has-preview {
          border-style: solid;
          border-color: rgba(16,185,129,0.5);
          background: rgba(16,185,129,0.05);
        }
        .avatar-upload-zone.has-preview:hover {
          border-color: var(--color-green);
          background: rgba(16,185,129,0.08);
        }
        .auz-icon {
          width: 48px; height: 48px;
          border-radius: 50%;
          background: var(--accent-bg);
          border: 1px solid var(--border-active);
          display: flex; align-items: center; justify-content: center;
          color: var(--accent);
          flex-shrink: 0;
          transition: transform 0.2s;
        }
        .avatar-upload-zone:hover .auz-icon { transform: scale(1.08); }
        .auz-text { display: flex; flex-direction: column; gap: 3px; }
        .auz-primary { font-size: var(--fs-sm); font-weight: 600; color: var(--text1); }
        .auz-hint    { font-size: var(--fs-xs); color: var(--text3); }
        .auz-preview-img {
          width: 48px; height: 48px;
          border-radius: 50%;
          overflow: hidden;
          border: 2px solid var(--color-green);
          flex-shrink: 0;
        }
        .auz-preview-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .auz-preview-info { display: flex; flex-direction: column; gap: 3px; flex: 1; min-width: 0; }
        .auz-fname {
          font-size: var(--fs-sm); font-weight: 600; color: var(--color-green);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .auz-change { font-size: var(--fs-xs); color: var(--text3); }
        .auz-clear {
          position: absolute; top: 8px; right: 8px;
          width: 22px; height: 22px;
          border-radius: 50%;
          border: 1px solid var(--border);
          background: var(--bg-surface);
          color: var(--text3);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          transition: all 0.15s;
        }
        .auz-clear:hover { color: var(--color-red); border-color: rgba(239,68,68,0.4); background: rgba(239,68,68,0.08); }

        /* ── Announcement bar card ── */
        .topbar-card {
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background: var(--bg-raised);
          overflow: hidden;
          transition: border-color .2s;
        }
        .topbar-card.is-on { border-color: var(--border-active); }
        .topbar-card-head {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
        }
        .topbar-card-icon {
          width: 34px; height: 34px;
          border-radius: var(--radius-md);
          display: flex; align-items: center; justify-content: center;
          background: var(--accent-bg);
          color: var(--accent);
          border: 1px solid var(--border-active);
          flex-shrink: 0;
        }
        .topbar-card-info { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
        .topbar-card-title { font-size: var(--fs-sm); font-weight: 600; color: var(--text1); }
        .topbar-card-sub { font-size: var(--fs-xs); color: var(--text3); }
        .topbar-card-body {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 14px;
          border-top: 1px solid var(--border);
        }
        .topbar-optional { color: var(--text3); font-weight: 400; }

        .switch {
          position: relative;
          width: 40px; height: 22px;
          border-radius: 999px;
          border: none;
          background: var(--border);
          cursor: pointer;
          flex-shrink: 0;
          transition: background .2s;
          padding: 0;
        }
        .switch.on { background: var(--accent); }
        .switch-knob {
          position: absolute;
          top: 3px; left: 3px;
          width: 16px; height: 16px;
          border-radius: 50%;
          background: #fff;
          transition: transform .2s;
        }
        .switch.on .switch-knob { transform: translateX(18px); }

        .topbar-preview { display: flex; flex-direction: column; gap: 6px; }
        .topbar-preview-label {
          font-size: var(--fs-xs);
          color: var(--text3);
          text-transform: uppercase;
          letter-spacing: .5px;
        }
        .topbar-preview-strip {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 10px;
          padding: 9px 12px;
          border-radius: var(--radius-md);
          background: #2f4a2f;
          color: #fff;
          font-size: var(--fs-xs);
        }
        .topbar-preview-strip b { color: #e8c98a; }
        .tp-divider { opacity: .5; }

        /* ── error + toast ── */
        .form-error {
          background: rgba(239, 68, 68, 0.12);
          border: 1px solid rgba(239, 68, 68, 0.35);
          color: #fca5a5;
          font-size: var(--fs-sm);
          padding: 8px 12px;
          border-radius: var(--radius-md);
        }
        .profile-toast {
          padding: 10px 14px;
          border-radius: var(--radius-md);
          font-size: var(--fs-sm);
          font-weight: 500;
        }
        .profile-toast.success {
          background: rgba(16,185,129,0.12);
          border: 1px solid rgba(16,185,129,0.35);
          color: var(--color-green);
        }
        .profile-toast.error {
          background: rgba(239,68,68,0.12);
          border: 1px solid rgba(239,68,68,0.35);
          color: #fca5a5;
        }

        @media (max-width: 1024px) {
          .profile-content { grid-template-columns: 1fr; }
          .profile-hero { flex-direction: column; gap: var(--space-lg); text-align: center; }
          .profile-hero-left { justify-content: center; }
        }
        @media (max-width: 768px) {
          .profile-details-grid { grid-template-columns: 1fr; }
          .profile-hero { padding: var(--space-md); gap: var(--space-md); }
          .profile-hero-left { flex-direction: column; gap: var(--space-sm); }
          .profile-avatar { width: 48px; height: 48px; }
          .profile-hero h2 { font-size: var(--fs-xl); }
          .profile-tags { justify-content: center; margin-top: 8px; }
        }
      `}</style>
    </div>
  )
}